import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Pages } from '../../Pages'
import Button from '../common/form/inputs/Button'
import kitApi from '@/api/kit/KitApi'
import LinkedInUrlHelp from '../candidate/LinkedInUrlHelp'
import { useAuth } from '@/auth/AuthContext'

type FormState = {
    name: string
    workEmail: string
    jobTitle: string
    linkedinUrl: string
}

type FormErrors = Partial<Record<keyof FormState, string>>

const themeVars = {
    '--background': '210 33% 98%',
    '--foreground': '220 20% 20%',
    '--card': '0 0% 100%',
    '--card-foreground': '220 20% 20%',
    '--popover': '0 0% 100%',
    '--popover-foreground': '220 20% 20%',
    '--primary': '217 100% 50%',
    '--primary-foreground': '0 0% 100%',
    '--secondary': '210 25% 96%',
    '--secondary-foreground': '220 20% 30%',
    '--muted': '210 20% 94%',
    '--muted-foreground': '220 10% 50%',
    '--accent': '210 30% 95%',
    '--accent-foreground': '217 100% 45%',
    '--destructive': '0 84% 60%',
    '--destructive-foreground': '0 0% 100%',
    '--border': '210 20% 90%',
    '--input': '210 20% 90%',
    '--ring': '217 100% 50%',
    '--radius': '1.25rem',
    '--shadow-soft': '0 4px 24px -4px hsl(220 20% 20% / 0.08)',
    '--shadow-card': '0 8px 32px -8px hsl(220 20% 20% / 0.1)',
} as React.CSSProperties

const containerClass = 'mx-auto w-full max-w-[1200px] px-6'

export default function HiringManagerKitSignup(): React.JSX.Element {
    const navigate = useNavigate()
    const { user: authUser, loading: authLoading, refresh } = useAuth()
    const [formState, setFormState] = useState<FormState>({
        name: '',
        workEmail: '',
        jobTitle: '',
        linkedinUrl: '',
    })
    const [errors, setErrors] = useState<FormErrors>({})
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)

    const handleChange =
        (field: keyof FormState) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value
            setFormState((prev) => ({ ...prev, [field]: value }))
            setSubmitError(null)
            if (errors[field]) {
                setErrors((prev) => {
                    const next = { ...prev }
                    delete next[field]
                    return next
                })
            }
        }

    const validate = (
        name: string,
        workEmail: string,
        jobTitle: string,
        linkedinUrl: string,
    ): FormErrors => {
        const nextErrors: FormErrors = {}

        if (!name) {
            nextErrors.name = 'Name is required.'
        }

        if (!workEmail) {
            nextErrors.workEmail = 'Work email is required.'
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(workEmail)) {
            nextErrors.workEmail = 'Enter a valid email address.'
        }

        if (!jobTitle) {
            nextErrors.jobTitle = 'Job title is required.'
        }

        if (
            linkedinUrl &&
            !linkedinUrl.toLowerCase().includes('linkedin.com/in')
        ) {
            nextErrors.linkedinUrl =
                'Use your profile link that includes linkedin.com/in.'
        }

        return nextErrors
    }

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        const trimmedName = formState.name.trim()
        const trimmedEmail = formState.workEmail.trim()
        const trimmedJobTitle = formState.jobTitle.trim()
        const trimmedLinkedIn = formState.linkedinUrl.trim()

        const validationErrors = validate(
            trimmedName,
            trimmedEmail,
            trimmedJobTitle,
            trimmedLinkedIn,
        )
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors)
            return
        }

        setFormState({
            name: trimmedName,
            workEmail: trimmedEmail,
            jobTitle: trimmedJobTitle,
            linkedinUrl: trimmedLinkedIn,
        })
        setErrors({})
        setSubmitError(null)
        setIsSubmitting(true)

        try {
            const response = await kitApi.submitJobAdSignup({
                name: trimmedName,
                workEmail: trimmedEmail,
                jobTitle: trimmedJobTitle,
                linkedinUrl: trimmedLinkedIn || undefined,
            })

            if (response.requiresLogin) {
                const params = new URLSearchParams()
                params.set('email', trimmedEmail)
                params.set('step', 'choose-method')
                params.set('target', Pages.HiringManagerKit)
                navigate(`${Pages.Login}?${params.toString()}`, {
                    replace: true,
                })
                return
            }

            const userId =
                typeof response.userId === 'string'
                    ? response.userId.trim()
                    : ''

            if (!userId) {
                throw new Error('Signup succeeded but no user id was returned.')
            }

            await refresh()
            navigate(Pages.HiringManagerKit)
        } catch (error) {
            console.error('Failed to submit hiring manager kit form', error)
            const message =
                error instanceof Error
                    ? error.message
                    : 'Unable to submit your details right now.'
            setSubmitError(message)
        } finally {
            setIsSubmitting(false)
        }
    }

    useEffect(() => {
        if (authLoading) return
        if (authUser) {
            navigate(Pages.HiringManagerKit, { replace: true })
        }
    }, [authLoading, authUser, navigate])

    return (
        <div
            className="min-h-screen bg-background text-foreground font-sans antialiased flex flex-col"
            style={themeVars}
        >
            <header className="w-full py-6">
                <div
                    className={`${containerClass} flex items-center justify-center`}
                >
                    <img
                        src="/Contxt_Logo_Full_BLK_CROPPED.png"
                        alt="Contxt"
                        className="h-10 w-auto"
                    />
                </div>
            </header>
            <main className="flex-1">
                <section className="py-8 md:py-12">
                    <div className={containerClass}>
                        <div className="mx-auto w-full max-w-2xl">
                            <form
                                className="space-y-6 rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-card)] md:p-8"
                                onSubmit={handleSubmit}
                                noValidate
                            >
                                <div className="space-y-2">
                                    <h1 className="text-2xl font-semibold text-foreground">
                                        Speak to Kit about your next hire
                                    </h1>
                                    <p className="text-sm text-muted-foreground">
                                        Share a few details and Kit will help
                                        you get started.
                                    </p>
                                </div>
                                <div className="space-y-2">
                                    <label
                                        htmlFor="hm-name"
                                        className="text-sm font-semibold text-foreground"
                                    >
                                        Name
                                    </label>
                                    <input
                                        id="hm-name"
                                        name="name"
                                        type="text"
                                        autoComplete="name"
                                        value={formState.name}
                                        onChange={handleChange('name')}
                                        className="w-full rounded-xl border border-border bg-card px-4 py-3 text-base text-foreground shadow-inner focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
                                        placeholder="Jane Smith"
                                        required
                                    />
                                    {errors.name && (
                                        <p className="text-sm text-destructive">
                                            {errors.name}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label
                                        htmlFor="hm-work-email"
                                        className="text-sm font-semibold text-foreground"
                                    >
                                        Work email
                                    </label>
                                    <input
                                        id="hm-work-email"
                                        name="workEmail"
                                        type="email"
                                        inputMode="email"
                                        autoComplete="email"
                                        value={formState.workEmail}
                                        onChange={handleChange('workEmail')}
                                        className="w-full rounded-xl border border-border bg-card px-4 py-3 text-base text-foreground shadow-inner focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
                                        placeholder="you@company.com"
                                        required
                                    />
                                    {errors.workEmail && (
                                        <p className="text-sm text-destructive">
                                            {errors.workEmail}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label
                                        htmlFor="hm-job-title"
                                        className="text-sm font-semibold text-foreground"
                                    >
                                        Job title you are hiring for
                                    </label>
                                    <input
                                        id="hm-job-title"
                                        name="jobTitle"
                                        type="text"
                                        value={formState.jobTitle}
                                        onChange={handleChange('jobTitle')}
                                        className="w-full rounded-xl border border-border bg-card px-4 py-3 text-base text-foreground shadow-inner focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
                                        placeholder="Head of Marketing"
                                        required
                                    />
                                    {errors.jobTitle && (
                                        <p className="text-sm text-destructive">
                                            {errors.jobTitle}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label
                                        htmlFor="hm-linkedin"
                                        className="text-sm font-semibold text-foreground"
                                    >
                                        LinkedIn URL (optional)
                                    </label>
                                    <input
                                        id="hm-linkedin"
                                        name="linkedinUrl"
                                        type="url"
                                        inputMode="url"
                                        value={formState.linkedinUrl}
                                        onChange={handleChange('linkedinUrl')}
                                        className="w-full rounded-xl border border-border bg-card px-4 py-3 text-base text-foreground shadow-inner focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
                                        placeholder="https://www.linkedin.com/in/your-profile"
                                    />
                                    {errors.linkedinUrl && (
                                        <p className="text-sm text-destructive">
                                            {errors.linkedinUrl}
                                        </p>
                                    )}
                                </div>

                                <LinkedInUrlHelp />

                                {submitError ? (
                                    <p className="text-sm text-destructive">
                                        {submitError}
                                    </p>
                                ) : null}

                                <Button
                                    type="submit"
                                    style="primary"
                                    enabled={!isSubmitting}
                                    classes="w-full justify-center py-3 text-base shadow-lg shadow-ctxt-blue/20 hover:shadow-xl transition-all"
                                >
                                    {isSubmitting
                                        ? 'Submitting…'
                                        : 'Continue to Kit'}
                                </Button>
                            </form>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    )
}
